// TANDA AS-3 — EL ÚNICO PUENTE DEL FRONTEND A STOREKIT.
//
// Ninguna superficie habla con `@capgo/native-purchases` directamente: todas pasan por
// aquí. Ese aislamiento es lo que permite que la WEB no cambie ni de comportamiento ni de
// dependencias — el módulo nativo se pide con `import()` DINÁMICO dentro de la rama
// `IS_APP_BUILD`, exactamente el patrón de `lib/tokenStore.js`: webpack lo parte en un
// chunk que un navegador jamás solicita, y en el build web ninguna de las funciones de
// abajo llega nunca a evaluarlo.
//
// LAS TRES COSAS QUE ESTE ARCHIVO GARANTIZA
//
//   1. LOS PRECIOS LOS PINTA APPLE. Las superficies leen `priceString` (ya localizado por
//      Apple para el storefront del usuario) y, para los derivados, `price` + `currencyCode`
//      con `Intl.NumberFormat`. En build de app JAMÁS se pinta un precio de las constantes
//      de la web.
//
//   2. TODA COMPRA PAGADA LLEGA A `/payments/apple/verify`. Ver "EL CARTERO DE COMPRAS".
//
//   3. LA CANCELACIÓN DEL USUARIO ES SILENCIO. Cerrar la hoja de Apple no es un error:
//      no hay toast, no hay estado de error, no hay nada.
//
// EL CARTERO DE COMPRAS (el invariante innegociable)
//
// Semántica de finish del plugin instalado (@capgo/native-purchases 8.6.5, verificada en
// su fuente Swift):
//
//   · `purchaseProduct` finaliza la transacción SOLO si `autoAcknowledgePurchases` es true
//     (su valor por omisión). Aquí se pasa **false** a propósito: una transacción se
//     considera cerrada únicamente tras un 200 de /verify o un rechazo DEFINITIVO del
//     backend, y entonces se cierra a mano con `acknowledgePurchase`
//     (ios/.../NativePurchasesPlugin+PurchaseHandling.swift:19-25).
//
//   · El listener `transactionUpdated` NO deja elección: el plugin llama a
//     `transaction.finish()` ANTES de notificar (NativePurchasesPlugin.swift:55-59). O sea
//     que una transacción entregada por ese camino ya está finalizada cuando nos enteramos,
//     y StoreKit no la volverá a entregar. Por eso la cola durable de abajo NO es un lujo:
//     es la única red para el recibo que llega sin sesión o con el backend caído.
//
// La cola vive en localStorage (durable entre arranques de la app, igual que la señal de
// `lib/planSync.js`) y guarda el JWS firmado por Apple. Se drena: (a) al llegar el evento,
// (b) al montarse una sesión autenticada, (c) tras una compra o un "Restaurar compras".
// El backend es idempotente por `transactionId`, así que re-verificar es gratis.
//
// SIN SESIÓN NO SE VERIFICA: /verify exige auth (un invitado recibe 403). Si no hay tokens
// el recibo se queda encolado y se reintenta después del login. "Restaurar compras" es la
// segunda red.
import { IS_APP_BUILD } from './buildTarget';
import api from './api';
import { broadcastPlanChanged } from './planSync';
import { getTokens } from './tokenStore';
import { appleErrorIsDefinitive, appleErrorCode } from './appleErrorCopy';

// ── Los cuatro Product IDs — ESPEJO del backend ─────────────────────────────
//
// Fuente única: `APPLE_PRODUCT_MAP` en backend/config.py:602-607 (los IDs creados y
// verificados en App Store Connect). Si allá cambia uno, cambia aquí — y la prueba
// `purchases.as3.test.js` fija el espejo para que no se descubra en producción.
export const APPLE_PRODUCT_IDS = {
  pro_monthly: 'vc.studybuddy.app.pro.monthly',
  pro_annual: 'vc.studybuddy.app.pro.annual',
  pack_small: 'vc.studybuddy.app.pack.small',
  pack_large: 'vc.studybuddy.app.pack.large',
};

export const SUBSCRIPTION_KEYS = ['pro_monthly', 'pro_annual'];
export const PACK_KEYS = ['pack_small', 'pack_large'];

/** Estados observables del catálogo de productos (los que pintan las superficies). */
export const PRODUCTS_STATUS = { IDLE: 'idle', LOADING: 'loading', READY: 'ready', ERROR: 'error' };

// Namespace UUID del proyecto para el `appAccountToken` (UUIDv5 determinista sobre el id
// de usuario). Es una constante FIJA y nueva de StudyBuddy: cambiarla rompería la
// correspondencia entre las compras ya hechas y la cuenta que las hizo. Apple exige
// formato UUID estricto para `appAccountToken` (StoreKit 2), de ahí el v5 y no un hash
// suelto.
export const APP_ACCOUNT_NAMESPACE = '9a3f7d2e-5c41-4b8a-9f16-2d0e7c48b5a3';

// Cola durable de recibos pagados pendientes de verificar (ver "EL CARTERO DE COMPRAS").
const PENDING_KEY = 'sb_apple_pending_verify';

// ════════════════════════════ UUIDv5 (SHA-1, sin dependencias) ════════════════════════════
//
// Se implementa a mano por dos razones concretas: (1) `crypto.subtle` sólo existe en
// contextos seguros y es asíncrono —el token se necesita en el momento de armar la compra—,
// y (2) meter un paquete de uuid al bundle por 40 líneas de algoritmo público sería peso
// nuevo para la web, que jamás lo usa.

function sha1Bytes(bytes) {
  const ml = bytes.length * 8;
  const withPad = new Uint8Array((((bytes.length + 8) >> 6) + 1) * 64);
  withPad.set(bytes);
  withPad[bytes.length] = 0x80;
  const view = new DataView(withPad.buffer);
  view.setUint32(withPad.length - 4, ml >>> 0, false);
  view.setUint32(withPad.length - 8, Math.floor(ml / 4294967296), false);

  let h0 = 0x67452301, h1 = 0xEFCDAB89, h2 = 0x98BADCFE, h3 = 0x10325476, h4 = 0xC3D2E1F0;
  const w = new Uint32Array(80);
  for (let i = 0; i < withPad.length; i += 64) {
    for (let j = 0; j < 16; j += 1) w[j] = view.getUint32(i + j * 4, false);
    for (let j = 16; j < 80; j += 1) {
      const n = w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16];
      w[j] = (n << 1) | (n >>> 31);
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4;
    for (let j = 0; j < 80; j += 1) {
      let f, k;
      if (j < 20) { f = (b & c) | (~b & d); k = 0x5A827999; }
      else if (j < 40) { f = b ^ c ^ d; k = 0x6ED9EBA1; }
      else if (j < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8F1BBCDC; }
      else { f = b ^ c ^ d; k = 0xCA62C1D6; }
      const tmp = (((a << 5) | (a >>> 27)) + f + e + k + w[j]) >>> 0;
      e = d; d = c; c = ((b << 30) | (b >>> 2)) >>> 0; b = a; a = tmp;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0; h4 = (h4 + e) >>> 0;
  }
  const out = new Uint8Array(20);
  const ov = new DataView(out.buffer);
  [h0, h1, h2, h3, h4].forEach((h, i) => ov.setUint32(i * 4, h, false));
  return out;
}

function uuidToBytes(uuid) {
  const hex = String(uuid).replace(/[^0-9a-fA-F]/g, '');
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
}

/**
 * UUIDv5 determinista: el MISMO id de usuario produce SIEMPRE el mismo token, y dos
 * usuarios distintos jamás colisionan. Es lo que Apple guarda en la transacción
 * (`appAccountToken`) y lo que permite reconocer al dueño de un recibo sin mandarle a
 * Apple ni el correo ni el id crudo.
 */
export function appAccountTokenFor(userId) {
  if (!userId) return null;
  const name = String(userId);
  const nameBytes = new Uint8Array(name.length * 3);
  let n = 0;
  for (let i = 0; i < name.length; i += 1) {
    const c = name.charCodeAt(i);
    if (c < 0x80) { nameBytes[n++] = c; }
    else if (c < 0x800) { nameBytes[n++] = 0xC0 | (c >> 6); nameBytes[n++] = 0x80 | (c & 63); }
    else { nameBytes[n++] = 0xE0 | (c >> 12); nameBytes[n++] = 0x80 | ((c >> 6) & 63); nameBytes[n++] = 0x80 | (c & 63); }
  }
  const ns = uuidToBytes(APP_ACCOUNT_NAMESPACE);
  const buf = new Uint8Array(16 + n);
  buf.set(ns, 0);
  buf.set(nameBytes.subarray(0, n), 16);
  const hash = sha1Bytes(buf);
  hash[6] = (hash[6] & 0x0f) | 0x50;      // versión 5
  hash[8] = (hash[8] & 0x3f) | 0x80;      // variante RFC 4122
  const hex = Array.from(hash.subarray(0, 16)).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// ════════════════════════════ el plugin, siempre por aquí ════════════════════════════

// Devuelve un OBJETO ENVOLTORIO, nunca el plugin pelado — misma razón que en
// `tokenStore.getSecureStorage()`: el proxy de `registerPlugin` convierte CUALQUIER acceso
// a una propiedad en una llamada nativa, incluido `.then`, y un proxy que se vuelve valor
// de resolución de una promesa cuelga para siempre.
async function nativo() {
  if (!IS_APP_BUILD) return null;
  const mod = await import('@capgo/native-purchases');
  return { plugin: mod.NativePurchases };
}

// ════════════════════════════ catálogo de productos ════════════════════════════

let estado = PRODUCTS_STATUS.IDLE;
let productos = {};            // clave interna -> Product de StoreKit
let entitled = null;           // Product ID de la suscripción VIGENTE (o null si no se sabe)
let cargaEnVuelo = null;
const oyentes = new Set();

function avisar() {
  oyentes.forEach((cb) => { try { cb(snapshot()); } catch (e) { /* un oyente roto no rompe el resto */ } });
}

/** El estado observable que pintan las superficies. */
export function snapshot() {
  return { status: estado, products: productos, entitledProductId: entitled };
}

/** Suscripción al catálogo. Devuelve la función para darse de baja. */
export function subscribeProducts(cb) {
  oyentes.add(cb);
  return () => oyentes.delete(cb);
}

/**
 * Carga (o recarga) los cuatro productos desde StoreKit.
 *
 * `isBillingSupported()` es el pre-check: si el aparato no puede cobrar, el catálogo queda
 * en `error` y la superficie pinta su estado honesto (F-B) en vez de una pantalla rota.
 */
export async function loadProducts() {
  if (!IS_APP_BUILD) return snapshot();
  if (cargaEnVuelo) return cargaEnVuelo;
  estado = PRODUCTS_STATUS.LOADING;
  avisar();
  cargaEnVuelo = (async () => {
    try {
      const { plugin } = await nativo();
      const soporte = await plugin.isBillingSupported();
      if (!soporte?.isBillingSupported) throw new Error('billing not supported');
      // getProducts (plural) A PROPÓSITO: el propio plugin documenta que varias llamadas
      // concurrentes a getProduct producen una carrera en el cliente nativo.
      const res = await plugin.getProducts({ productIdentifiers: Object.values(APPLE_PRODUCT_IDS) });
      const porId = {};
      (res?.products || []).forEach((p) => { if (p?.identifier) porId[p.identifier] = p; });
      const siguiente = {};
      Object.entries(APPLE_PRODUCT_IDS).forEach(([clave, id]) => {
        if (porId[id]) siguiente[clave] = porId[id];
      });
      // Sin los cuatro productos no se pinta media pantalla de precios: es error.
      if (Object.keys(siguiente).length !== Object.keys(APPLE_PRODUCT_IDS).length) {
        throw new Error('incomplete product catalog');
      }
      productos = siguiente;
      estado = PRODUCTS_STATUS.READY;
      await refreshEntitlement(plugin);
    } catch (e) {
      estado = PRODUCTS_STATUS.ERROR;
    } finally {
      cargaEnVuelo = null;
      avisar();
    }
    return snapshot();
  })();
  return cargaEnVuelo;
}

/**
 * Qué producto de suscripción tiene VIGENTE este aparato, según StoreKit (no según
 * nuestro backend). Es lo que permite el crossgrade mensual↔anual sin extender ningún
 * endpoint: `currentEntitlements` ya sabe cuál de los dos está activo. Si falla, queda
 * `null` y la superficie se comporta como hoy.
 */
async function refreshEntitlement(plugin) {
  try {
    const res = await plugin.getPurchases({ onlyCurrentEntitlements: true });
    const subs = (res?.purchases || []).filter(p => p?.productIdentifier === APPLE_PRODUCT_IDS.pro_monthly
      || p?.productIdentifier === APPLE_PRODUCT_IDS.pro_annual);
    entitled = subs.length ? subs[subs.length - 1].productIdentifier : null;
  } catch (e) {
    entitled = null;
  }
}

// ════════════════════════════ la cola durable de recibos ════════════════════════════

function leerCola() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(x => x && x.jws) : [];
  } catch (e) {
    return [];
  }
}

function escribirCola(items) {
  try {
    if (!items.length) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(items.slice(-25)));
  } catch (e) { /* almacenamiento no disponible — mejor esfuerzo */ }
}

function encolar(txn) {
  if (!txn?.jwsRepresentation) return;
  const cola = leerCola();
  if (cola.some(x => x.transactionId && x.transactionId === txn.transactionId)) return;
  cola.push({
    jws: txn.jwsRepresentation,
    transactionId: txn.transactionId || null,
    productId: txn.productIdentifier || null,
  });
  escribirCola(cola);
}

function desencolar(transactionId, jws) {
  const cola = leerCola().filter(x => !(
    (transactionId && x.transactionId === transactionId) || (!transactionId && x.jws === jws)
  ));
  escribirCola(cola);
}

/** Sólo para pruebas y diagnóstico: cuántos recibos pagados siguen sin confirmarse. */
export function pendingVerifications() {
  return leerCola();
}

// ════════════════════════════ /payments/apple/verify ════════════════════════════

let onApplied = null;   // lo pone el arranque de la app (AuthContext): refresca /auth/me
let usuarioActual = null;   // el id de la identidad viva, para el appAccountToken

/**
 * La identidad viva, puesta por el bootstrap de la app (AuthContext) en cada frontera de
 * sesión. Existe para que una superficie de compra NO tenga que colgarse del contexto de
 * auth sólo para armar el `appAccountToken` — el modal de paquetes se monta en tres
 * pantallas y no debe estrenar dependencias por esto.
 */
export function setPurchaseUser(userId) {
  usuarioActual = userId || null;
}

/**
 * Manda un JWS a nuestro backend. Devuelve `{ ok, data, error }`.
 *
 * Con 200: se anuncia el cambio de plan por el MISMO carril que usa el retorno de Stripe
 * (`broadcastPlanChanged` → invalidación de `screenDataCache` + re-poll del Panel) y se
 * refresca el usuario. Con rechazo DEFINITIVO el recibo sale de la cola (reintentarlo mil
 * veces no lo va a arreglar). Con fallo TRANSITORIO se queda encolado.
 */
async function verificar(entry) {
  try {
    const res = await api.post('/payments/apple/verify', { signed_transaction_info: entry.jws });
    desencolar(entry.transactionId, entry.jws);
    await finalizar(entry.transactionId);
    broadcastPlanChanged();
    if (onApplied) { try { await onApplied(); } catch (e) { /* el refresco jamás rompe el cobro */ } }
    // 200 —incluido `already_processed`, que es "esto ya estaba acreditado"— cierra el
    // recibo: fuera de la cola y finalizado con StoreKit.
    bitacora('confirmado', entry, res?.data?.status);
    return { ok: true, data: res?.data };
  } catch (err) {
    if (appleErrorIsDefinitive(err)) {
      desencolar(entry.transactionId, entry.jws);
      await finalizar(entry.transactionId);
      bitacora('rechazo definitivo', entry, appleErrorCode(err));
    } else {
      // TRANSITORIO: el recibo SE QUEDA en la cola y se reintenta en el próximo drenaje.
      bitacora('pendiente (transitorio)', entry, appleErrorCode(err));
    }
    return { ok: false, error: err };
  }
}

/**
 * La bitácora del cartero: DISCRETA y de consola. El cartero corre de fondo —al arrancar
 * la app, al entrar la sesión, cuando Apple entrega una transacción vieja—, así que jamás
 * pinta un toast ni un estado: nadie le pidió nada al usuario en ese momento. Lo único
 * que deja es un renglón para poder reconstruir después qué pasó con cada recibo.
 */
function bitacora(desenlace, entry, detalle) {
  try {
    // eslint-disable-next-line no-console
    console.info(`[apple][cartero] ${desenlace}`,
      { transactionId: entry?.transactionId || null, productId: entry?.productId || null, detalle: detalle || null });
  } catch (e) { /* una bitácora jamás rompe un cobro */ }
}

/**
 * Cierra la transacción con StoreKit. Se llama SÓLO cuando el recibo ya llegó a su
 * destino (200) o el backend lo rechazó definitivamente. Una transacción entregada por
 * `transactionUpdated` ya viene finalizada por el plugin: ahí esto es un no-op que
 * rebota, y por eso el error se traga.
 */
async function finalizar(transactionId) {
  if (!IS_APP_BUILD || !transactionId) return;
  try {
    const { plugin } = await nativo();
    await plugin.acknowledgePurchase({ purchaseToken: String(transactionId) });
  } catch (e) { /* ya estaba finalizada (o el plugin no la encuentra): nada que hacer */ }
}

/** ¿Hay sesión? /verify exige auth: sin tokens el recibo espera al login. */
async function haySesion() {
  try {
    const t = await getTokens();
    return !!t?.access_token;
  } catch (e) {
    return false;
  }
}

/**
 * Drena la cola. Devuelve `{ applied, lastError }` — `applied` es cuántos recibos
 * cambiaron algo de verdad en el servidor.
 */
export async function flushPendingVerifications() {
  if (!IS_APP_BUILD) return { applied: 0, lastError: null };
  const cola = leerCola();
  if (!cola.length) return { applied: 0, lastError: null };
  if (!await haySesion()) return { applied: 0, lastError: null };
  let applied = 0;
  let lastError = null;
  for (const entry of cola) {
    // eslint-disable-next-line no-await-in-loop
    const r = await verificar(entry);
    if (r.ok) applied += 1;
    else lastError = r.error;
  }
  return { applied, lastError };
}

// ════════════════════════════ comprar ════════════════════════════

/**
 * ¿Este error es "el usuario cerró la hoja de Apple"? El plugin rechaza con el literal
 * "User cancelled" (NativePurchasesPlugin+PurchaseHandling.swift:32) y StoreKit describe
 * su propio `userCancelled` con la misma palabra. Una cancelación NO es un error: se
 * devuelve `cancelled` y la superficie no pinta absolutamente nada.
 */
function esCancelacion(err) {
  const msg = String(err?.message || err || '');
  return /cancel/i.test(msg);
}

/**
 * Una compra pendiente de aprobación (Ask to Buy / "Transaction pending") tampoco es un
 * error: Apple la entregará más tarde por `Transaction.updates` y el cartero la verificará.
 */
function esPendiente(err) {
  return /pending/i.test(String(err?.message || err || ''));
}

/**
 * Compra por clave interna (`pro_monthly` | `pro_annual` | `pack_small` | `pack_large`).
 *
 * Devuelve `{ status: 'ok' | 'cancelled' | 'pending' | 'error', data, error }`.
 *
 * `autoAcknowledgePurchases: false` es deliberado: la transacción se cierra sólo cuando el
 * recibo llegó a nuestro backend (ver "EL CARTERO DE COMPRAS").
 */
export async function purchase(productKey, { userId } = {}) {
  if (!IS_APP_BUILD) return { status: 'error', error: new Error('purchases are native-only') };
  const productIdentifier = APPLE_PRODUCT_IDS[productKey];
  if (!productIdentifier) return { status: 'error', error: new Error(`unknown product key ${productKey}`) };

  let txn;
  try {
    const { plugin } = await nativo();
    const opciones = { productIdentifier, autoAcknowledgePurchases: false };
    const token = appAccountTokenFor(userId || usuarioActual);
    if (token) opciones.appAccountToken = token;
    txn = await plugin.purchaseProduct(opciones);
  } catch (err) {
    if (esCancelacion(err)) return { status: 'cancelled' };
    if (esPendiente(err)) return { status: 'pending' };
    return { status: 'error', error: err };
  }

  // Pagada. A partir de aquí el recibo NO se pierde: primero se encola (durable), luego
  // se intenta verificar. Si la verificación se cae, el cartero lo reintenta.
  encolar(txn);
  const r = await verificar({
    jws: txn?.jwsRepresentation,
    transactionId: txn?.transactionId || null,
    productId: txn?.productIdentifier || null,
  });
  if (r.ok) {
    await loadProducts();     // el entitlement vigente pudo cambiar (crossgrade)
    return { status: 'ok', data: r.data };
  }
  return { status: 'error', error: r.error };
}

// ════════════════════════════ restaurar ════════════════════════════

/**
 * "Restaurar compras": `AppStore.sync()` y después TODA transacción que StoreKit reconozca
 * en este aparato se manda a /verify (idempotente por `transactionId`).
 *
 * Devuelve `{ status: 'ok' | 'error', applied, error }`. `applied === 0` significa "no
 * había nada que restaurar" — el copy de `pricing:iap.restore_empty`.
 */
export async function restore() {
  if (!IS_APP_BUILD) return { status: 'error', applied: 0, error: new Error('purchases are native-only') };
  let compras = [];
  try {
    const { plugin } = await nativo();
    await plugin.restorePurchases();
    // restorePurchases() del plugin no devuelve nada (es AppStore.sync a secas): el
    // inventario con sus JWS sale de getPurchases.
    // post-t7-2-dinero (B1, firma 2): SOLO lo vigente (`currentEntitlements`, la misma opcion que
    // ya usa `refreshEntitlement`). Una suscripcion vencida o reembolsada no viaja; si viajara,
    // el servidor la contesta `already_processed` sin tocar nada. Los paquetes sin verificar
    // siguen llegando por la cola durable de abajo.
    const res = await plugin.getPurchases({ onlyCurrentEntitlements: true });
    compras = (res?.purchases || []).filter(p => p?.jwsRepresentation);
  } catch (err) {
    return { status: 'error', applied: 0, error: err };
  }

  let applied = 0;
  let lastError = null;
  for (const c of compras) {
    // eslint-disable-next-line no-await-in-loop
    const r = await verificar({
      jws: c.jwsRepresentation,
      transactionId: c.transactionId || null,
      productId: c.productIdentifier || null,
    });
    if (r.ok) {
      // `already_processed` = el recibo ya estaba acreditado: restaurar no cambió nada.
      if (r.data?.status !== 'already_processed') applied += 1;
    } else {
      lastError = r.error;
    }
  }
  // Y de paso, lo que quedara pendiente de antes.
  const pend = await flushPendingVerifications();
  applied += pend.applied;
  if (!applied && lastError) return { status: 'error', applied: 0, error: lastError };
  await loadProducts();
  return { status: 'ok', applied, error: null };
}

// ════════════════════════════ gestión nativa de suscripciones ════════════════════════════

/** Abre la pantalla de Suscripciones del App Store (Settings, rama `apple`). */
export async function openManageSubscriptions() {
  if (!IS_APP_BUILD) return { status: 'error' };
  try {
    const { plugin } = await nativo();
    await plugin.manageSubscriptions();
    return { status: 'ok' };
  } catch (err) {
    return { status: 'error', error: err };
  }
}

// ════════════════════════════ el arranque del cartero ════════════════════════════

/**
 * Registra el listener de StoreKit y drena lo que hubiera pendiente. Lo llama el bootstrap
 * app-only del frontend (contexts/AuthContext.js), junto a los demás singletons de la app.
 *
 * El listener ENCOLA siempre (aunque no haya sesión) y sólo después intenta verificar: por
 * eso una compra hecha con la sesión cerrada no se pierde — se cobra en el siguiente login.
 */
export async function startPurchaseCourier({ onApplied: cb } = {}) {
  if (!IS_APP_BUILD) return () => {};
  onApplied = cb || null;
  let handle = null;
  try {
    const { plugin } = await nativo();
    handle = await plugin.addListener('transactionUpdated', (txn) => {
      encolar(txn);
      flushPendingVerifications().catch(() => {});
    });
  } catch (e) { /* sin listener queda la cola + Restaurar compras */ }
  flushPendingVerifications().catch(() => {});
  return () => {
    try { if (handle?.remove) handle.remove(); } catch (e) { /* nada que hacer */ }
    onApplied = null;
  };
}

/** Sólo para pruebas: devuelve el módulo a su estado de arranque. */
export function __resetForTests() {
  estado = PRODUCTS_STATUS.IDLE;
  productos = {};
  entitled = null;
  cargaEnVuelo = null;
  onApplied = null;
  usuarioActual = null;
  oyentes.clear();
  try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* ignorado */ }
}

export const _PENDING_KEY = PENDING_KEY;
